import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setLanguage } from "../redux/features/language/languageSlice";
import type { LanguageCode } from "../redux/features/language/languageTypes";
import type { RootState } from "../redux/store";
import { buildAppLink } from "../utils/landingRedirect";
import { quantCopy } from "./quantLandingCopy";
import marketingContent from "./marketingContent.json";
import marketingRoutes from "./marketingRoutes.json";
import showcaseCopy from "./showcaseCopy.json";
import narrativeCopy from "./siteNarrativeCopy.json";
import founderCopy from "./founderStoryCopy.json";
import internationalCopy from "./internationalMarketingCopy.json";
import MarketingLanguageMenu from "./MarketingLanguageMenu";
import { isInternationalLanguage, marketingLanguageHref, MarketingLanguage, InternationalLanguage } from "./marketingLocalePaths";
import "./QuantLandingPage.css";
import "./MarketingPages.css";
import "./MarketingRoutes.css";
import "./ShowcaseStories.css";
import "./InternationalMarketing.css";

export const bookingHref = "https://calendly.com/shonnli-youwo/new-meeting-1";
const brandSurfaceStyle = {
  "--q-fabric": 'url("/brand/youwo-horizon-fabric-v2.webp")',
} as React.CSSProperties;
const logoMaskStyle: React.CSSProperties = {
  WebkitMask: 'url("/logo/logo.png") center / contain no-repeat',
  mask: 'url("/logo/logo.png") center / contain no-repeat',
};

export function Arrow({ diagonal = false }: { diagonal?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d={diagonal ? "M6 18 18 6M6 6h12v12" : "M4 12h16m-6-6 6 6-6 6"} />
    </svg>
  );
}

function Brand({ href = "/" }: { href?: string }) {
  return (
    <a className="quant-brand" href={href} aria-label="YouWo">
      <span style={logoMaskStyle} aria-hidden="true" />
      <strong>YouWo</strong>
    </a>
  );
}

export const useMarketingCopy = () => {
  const language = useSelector((state: RootState) => state.language.current);
  return {
    language,
    routes: marketingRoutes[language] ?? marketingRoutes.en,
    copy: quantCopy[language] ?? quantCopy.en,
    content: marketingContent[language] ?? marketingContent.en,
  };
};

export default function MarketingShell({
  children,
  active,
  variant,
  localeOverride,
}: {
  children: React.ReactNode;
  active?: "business" | "product" | "learnflow" | "mission" | "story" | "journal";
  variant?: "voyage" | "business-home";
  localeOverride?: InternationalLanguage;
}) {
  const dispatch = useDispatch();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const { i18n } = useTranslation();
  const baseLanguage = useSelector(
    (state: RootState) => state.language.current,
  );
  const currentLanguage: MarketingLanguage = localeOverride ?? baseLanguage;
  const copy = quantCopy[baseLanguage] ?? quantCopy.en;
  const localized = localeOverride ? internationalCopy[localeOverride] : null;

  React.useEffect(() => {
    document.documentElement.lang =
      currentLanguage === "zh" ? "zh-CN" : currentLanguage;
  }, [currentLanguage]);

  React.useEffect(() => {
    // Hash targets must be resolved after the client-rendered page exists.
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1);
      if (hash) document.getElementById(hash)?.scrollIntoView();
    };
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, []);

  const handleLanguageChange = (language: MarketingLanguage) => {
    if (language === currentLanguage) return;
    if (isInternationalLanguage(language)) {
      window.location.assign(marketingLanguageHref(language, window.location.pathname));
      return;
    }
    dispatch(setLanguage(language as LanguageCode));
    i18n.changeLanguage(language);
    if (localeOverride) window.location.assign(marketingLanguageHref(language, window.location.pathname));
  };

  const content = marketingContent[baseLanguage] ?? marketingContent.en;
  const routes = marketingRoutes[baseLanguage] ?? marketingRoutes.en;
  const showcase = showcaseCopy[baseLanguage] ?? showcaseCopy.en;
  const narrativeNav = (narrativeCopy[baseLanguage] ?? narrativeCopy.en).nav;
  const founderNav = (founderCopy[baseLanguage] ?? founderCopy.en).navLabel;
  const localePrefix = localeOverride ? `/${localeOverride}` : "";
  return (
    <div className={`quant-page${variant ? ` quant-${variant}` : ""}`} data-language={currentLanguage} style={brandSurfaceStyle}>
      <a className="quant-skip" href="#main-content">
        {copy.skip}
      </a>
      <header className="quant-header">
        <div className="quant-wrap quant-nav">
          <Brand href={localePrefix || "/"} />
          <nav
            id="quant-navigation"
            className={`quant-links${menuOpen ? " is-open" : ""}`}
            aria-label={copy.nav.label}
          >
            <a href={`${localePrefix}/business`} aria-current={active === "business" ? "page" : undefined}>{localized?.nav.business ?? content.nav.business}</a>
            <a
              href={`${localePrefix}/product`}
              aria-current={active === "product" ? "page" : undefined}
            >
              {localized?.nav.product ?? content.nav.product}
            </a>
            <a href={`${localePrefix}/learnflow`} aria-current={active === "learnflow" ? "page" : undefined}>Learnflow</a>
            <a href="/blog#case-studies" aria-current={active === "journal" ? "page" : undefined}>{localized?.nav.cases ?? narrativeNav.cases}</a>
            <a
              href="/mission"
              aria-current={active === "mission" ? "page" : undefined}
            >
              {localized?.nav.mission ?? routes.navMission}
            </a>
            <a href="/story" aria-current={active === "story" ? "page" : undefined}>{localized?.nav.story ?? narrativeNav.story}</a>
          </nav>
          <button type="button" className="quant-menu" aria-controls="quant-navigation" aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close navigation" : "Open navigation"} onClick={() => setMenuOpen(open => !open)}>
            <span aria-hidden="true">{menuOpen ? "×" : "☰"}</span>
          </button>
          <div className="quant-nav-actions">
            <MarketingLanguageMenu value={currentLanguage} label={localized?.nav.language ?? copy.nav.language} onChange={handleLanguageChange} />
            <a
              className="quant-button quant-nav-book"
              href={active === "learnflow" ? "https://learnflow.youwo.ai/" : active === "product" ? buildAppLink() : bookingHref}
              target={active === "product" || active === "learnflow" ? undefined : "_blank"}
              rel={active === "product" || active === "learnflow" ? undefined : "noopener noreferrer"}
            >
              {localized?.nav.getStarted ?? narrativeNav.getStarted}
            </a>
          </div>
        </div>
      </header>

      {children}
      <footer className="quant-wrap quant-footer">
        <div>
          <Brand href={localePrefix || "/"} />
          <p>{localized ? "YouWo — 由我" : showcase.meaning}</p>
          <a className="quant-footer-email" href="mailto:info@youwo.ai">info@youwo.ai</a>
        </div>
        <nav aria-label={copy.footer.label}>
          <a href="/mission">{localized?.nav.mission ?? routes.navMission}</a>
          <a href="/story">{localized?.nav.story ?? showcase.storyNav}</a>
          <a href="/founder">{founderNav}</a>
          <a href={`${localePrefix}/business`}>{localized?.nav.business ?? content.nav.business}</a>
          <a href={`${localePrefix}/product`}>{localized?.nav.product ?? content.nav.product}</a>
          <a href={`${localePrefix}/learnflow`}>Learnflow</a>
          <a href="/blog">{localized?.nav.cases ?? showcase.journalNav}</a>
          <a href="/privacy-policy">{copy.footer.privacy}</a>
          <a href="/terms-of-service">{copy.footer.terms}</a>
          <a href="/support">{copy.footer.support}</a>
          <a
            href="https://apps.apple.com/app/youwo-ai-agent/id6760689478"
            target="_blank"
            rel="noopener noreferrer"
          >
            iOS
          </a>
          <a
            href="https://downloads.youwo.ai/mac/YouWo-latest.dmg"
            target="_blank"
            rel="noopener noreferrer"
          >
            Mac
          </a>
        </nav>
        <span>© {new Date().getFullYear()} YouWo</span>
      </footer>
    </div>
  );
}
